"use client";

import {
  IconBolt,
  IconBrandAndroid,
  IconBrandApple,
  IconBrandReactNative,
  IconCloud,
  IconComponents,
  IconDatabase,
  IconServer,
  IconShieldLock,
  IconWebhook,
  IconWorld,
  IconWorldUpload,
} from "@tabler/icons-react";
import { createContext, type ReactNode, useContext } from "react";
import RadioCard from "src/components/form/Radio/RadioCard";
import RadioGroup from "src/components/form/Radio/RadioGroup";
import { useAppRouter, useSearchParams } from "src/lib/Navigation";
import styles from "./SetupPathPicker.module.scss";

const ICONS = {
  instant: IconBolt,
  webhook: IconWebhook,
  server: IconServer,
  signed: IconShieldLock,
  public: IconWorldUpload,
  expo: IconBrandReactNative,
  ios: IconBrandApple,
  android: IconBrandAndroid,
  web: IconWorld,
  cdn: IconCloud,
  components: IconComponents,
  storage: IconDatabase,
};

export interface SetupPathOption {
  value: string;
  label: string;
  description: string;
  icon: keyof typeof ICONS;
}

/** The default query parameter that links straight to one path, such as `?setup=public`. */
const SEARCH_PARAM = "setup";

const SetupPathContext = createContext<{ value: string; select: (next: string) => void } | null>(
  null,
);

/**
 * Radio cards that choose one way through a docs page. Only the chosen `SetupPath` renders, so the
 * page (and its table of contents) shows a single set of steps. The choice is kept in the URL so it
 * can be linked to; the Markdown export keeps every path. A page with more than one picker gives
 * each its own `param`.
 */
export default function SetupPathPicker({
  label,
  options,
  param = SEARCH_PARAM,
  children,
}: {
  label: string;
  options: SetupPathOption[];
  /** The query parameter that keeps this picker's choice. */
  param?: string;
  children: ReactNode;
}) {
  const searchParams = useSearchParams();
  const router = useAppRouter();
  const linked = searchParams.get(param);
  const value = options.find((option) => option.value === linked)?.value ?? options[0]!.value;

  const select = (next: string | undefined) => {
    if (!next || !options.some((option) => option.value === next)) return;
    const url = new URL(window.location.href);
    url.searchParams.set(param, next);
    void router.replace(`${url.pathname}${url.search}${url.hash}`, { scroll: false });
  };

  return (
    <SetupPathContext value={{ value, select }}>
      <div className={styles.container}>
        <RadioGroup
          className={styles.options}
          ariaLabel={label}
          value={value}
          onValueChange={select}
          // Four options sit in two rows of two rather than leaving one alone on a row.
          columns={options.length === 3 ? 3 : 2}
        >
          {options.map(({ value: optionValue, label: optionLabel, description, icon }) => {
            const Icon = ICONS[icon];
            return (
              <RadioCard
                key={optionValue}
                value={optionValue}
                label={optionLabel}
                description={description}
                icon={<Icon size={18} aria-hidden={true} />}
              />
            );
          })}
        </RadioGroup>
      </div>
      {children}
    </SetupPathContext>
  );
}

/**
 * The option chosen in the page's `SetupPathPicker` and a way to choose another, or `null` on a page
 * that has none, for what shows the choice itself (the upload flow's boxes).
 */
function useSetupPath() {
  return useContext(SetupPathContext);
}

/** The steps for one `SetupPathPicker` option, shown only while it is chosen. */
export function SetupPath({ value, children }: { value: string; children: ReactNode }) {
  const selected = useContext(SetupPathContext);
  if (selected === null) throw new Error("SetupPath must be rendered inside SetupPathPicker.");
  return selected.value === value ? children : null;
}
